import {
  Account,
  AccountCreateRequest,
  AccountDefaultSettings,
  AccountDefaultSettingsUpsert,
  AccountExportResponse,
  AccountProperty,
  AccountPropertySimple,
  AccountSimpleWithType,
  AccountUsersResponseItem,
  AssetAccountProperties,
  CSOptGroup,
  CSSelectOption,
  Category,
  NetworkFilters,
  NetworkToAccount,
  Product,
  RemoteDataSimple,
} from "types";
import { AccountsAPI, ProfileAPI } from "api";
import { UseMutationOptions, UseQueryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { UserPermissionType } from "consts/UserPermissionTypes";
import { useAuthState } from "context/auth";

type UseNetworkFiltersAsOptionsArgs = {
  accountId: string | number;
  defaultOption?: CSSelectOption;
};

type UseNetworkFiltersAsOptionsResult = RemoteDataSimple<(CSSelectOption | CSOptGroup)[]>;

type UpdateUserRoleArgs = {
  userId: number;
  roleName: string;
};

const queryKeys = {
  all: () => ["accounts"] as const,
  details: () => [...queryKeys.all(), "details"] as const,
  detail: (id: number) => [...queryKeys.details(), id] as const,
  defaultSettings: (id: number) => [...queryKeys.details(), id, "default-settings"] as const,
  lists: () => [...queryKeys.all(), "list"] as const,
  listAll: () => [...queryKeys.lists(), "all"] as const,
  listUsers: (id: number) => [...queryKeys.detail(id), "users"] as const,
  listCategories: (accountId: number) => [...queryKeys.detail(accountId), "categories"] as const,
  listProducts: (accountId: number) => [...queryKeys.detail(accountId), "products"] as const,
  listNetworks: (accountId: number) => [...queryKeys.lists(), "networks", { accountId }] as const,
  listNetworkFilters: (accountId: string | number) => [...queryKeys.lists(), "networkFilters", { accountId }] as const,
  getProperties: (accountId: string | number) => [...queryKeys.all(), "properties", accountId] as const,
  exportUsers: (accountId: string | number) => [...queryKeys.all(), "export-users", accountId],
  getAssetProperties: (accountId: string | number) => [...queryKeys.all(), "asset-properties", accountId] as const,
};

/**
 * Fetches all accounts which the user is authorized to view.
 * Returns "simple" account objects, which are a just the ID and name.
 * This is intended to populate dropdowns/select searches.
 */
export const useAllAccounts = <T = AccountSimpleWithType>(
  options: UseQueryOptions<AccountSimpleWithType[], unknown, T> = {},
) => useQuery<AccountSimpleWithType[], unknown, T>(queryKeys.listAll(), () => AccountsAPI.getAll(), options);

/**
 * Fetches the default settings for the given account.
 */
export const useAccountDefaultSettings = <T = AccountDefaultSettings>(
  accountId: number,
  options: UseQueryOptions<AccountDefaultSettings, unknown, T> = {},
) =>
  useQuery<AccountDefaultSettings, unknown, T>(
    queryKeys.defaultSettings(accountId),
    () => AccountsAPI.getDefaultSettings(accountId),
    options,
  );

/**
 * Fetches all networks for the provided account ID.
 */
export const useNetworks = <T = NetworkToAccount[]>(
  accountId: number,
  options: UseQueryOptions<NetworkToAccount[], unknown, T> = {},
) =>
  useQuery<NetworkToAccount[], unknown, T>(
    queryKeys.listNetworks(accountId),
    () => AccountsAPI.listNetworks(accountId),
    options,
  );

/**
 * Fetches all categories for the provided account ID.
 */
export const useCategories = <T = Category[]>(
  accountId: number,
  options: UseQueryOptions<Category[], unknown, T> = {},
) =>
  useQuery<Category[], unknown, T>(
    queryKeys.listCategories(accountId),
    () => AccountsAPI.listCategories(accountId),
    options,
  );

/**
 * Fetches all network filters for the provided account ID.
 */
export const useNetworkFilters = <T = NetworkFilters>(
  accountId: string | number,
  options: UseQueryOptions<NetworkFilters, unknown, T> = {},
) =>
  useQuery<NetworkFilters, unknown, T>(
    queryKeys.listNetworkFilters(accountId),
    () => AccountsAPI.listNetworkFilters(accountId),
    options,
  );

/**
 * Fetches all network filters for the provided account ID
 * and returns them as options for a Select element.
 * This is designed to work well with `CSFormInput.SelectAsync`.
 *
 * Additionally supports a top level "default" option.
 * We use this for the Dashboard filters to include a `Not Applied` option.
 *
 * See `useNetworkFilters` if you need the underlying data directly.
 */
export const useNetworkFiltersAsOptions = (
  { accountId, defaultOption }: UseNetworkFiltersAsOptionsArgs,
  options: UseQueryOptions<NetworkFilters, unknown, (CSOptGroup | CSSelectOption)[]> = {},
): UseNetworkFiltersAsOptionsResult => {
  return useNetworkFilters<(CSOptGroup | CSSelectOption)[]>(accountId, {
    ...options,
    select: (data) => [
      ...(defaultOption ? [defaultOption] : []),
      { name: "Accounts", options: data.accounts.map((x) => ({ text: x.name, value: x.id })) },
      { name: "Networks", options: data.networks.map((x) => ({ text: x.name, value: x.id })) },
    ],
  });
};

/**
 * Fetch account properties.
 */
export const useGetProperties = <T = AccountProperty[]>(
  accountId: string | number,
  options: UseQueryOptions<AccountProperty[], unknown, T> = {},
) =>
  useQuery<AccountProperty[], unknown, T>(
    queryKeys.getProperties(accountId),
    () => AccountsAPI.getProperties(accountId),
    options,
  );

/**
 * Fetch asset account properties.
 */
export const useGetAssetProperties = <T = AssetAccountProperties>(
  accountId: string | number,
  options: UseQueryOptions<AssetAccountProperties, unknown, T> = {},
) =>
  useQuery<AssetAccountProperties, unknown, T>(
    queryKeys.getAssetProperties(accountId),
    () => AccountsAPI.getAssetProperties(accountId),
    options,
  );

/**
 * Upsert Account Properties.
 */
export const useUpsertProperties = (
  accountId: string | number,
  options: UseMutationOptions<AccountPropertySimple[], unknown, AccountPropertySimple[]> = {},
) => useMutation((args) => AccountsAPI.upsertProperties(accountId, args), options);

/**
 * Upsert Account Default Settings Properties.
 */
export const useAccountDefaultSettingsUpsert = (
  accountId: number,
  options: UseMutationOptions<AccountDefaultSettings, unknown, AccountDefaultSettingsUpsert> = {},
) => useMutation((args) => AccountsAPI.upsertDefaultSettings(accountId, args), options);

/**
 * Fetches a list of users belonging to an account.
 */
export const useGetAccountUsers = <T = AccountUsersResponseItem>(
  id: number,
  options: UseQueryOptions<AccountUsersResponseItem[], unknown, T[]> = {},
) =>
  useQuery<AccountUsersResponseItem[], unknown, T[]>(queryKeys.listUsers(id), () => AccountsAPI.listUsers(id), options);

/**
 * Mutation to delete a user from an account.
 *
 * NOTE: This automatically updates the cache on success.
 */
export const useDeleteAccountUser = (accountId: number, options: UseMutationOptions<void, unknown, number> = {}) => {
  const queryClient = useQueryClient();
  return useMutation((userId) => AccountsAPI.deleteUTA(accountId, userId.toString()), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.removeQueries(queryKeys.listUsers(accountId));
      return options.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * Mutation to reinvite a user to an account.
 */
export const useReinviteAccountUser = (accountId: number, options: UseMutationOptions<void, unknown, number> = {}) =>
  useMutation((userId) => AccountsAPI.reinviteUser(accountId, userId), options);

/**
 * Mutation to update a user's role within an account.
 */
export const useAccountUserRole = (
  accountId: number,
  options: UseMutationOptions<void, unknown, UpdateUserRoleArgs> = {},
) => useMutation(({ userId, roleName }) => AccountsAPI.updateUTA(accountId, userId.toString(), roleName), options);

/**
 * Mutation to create a new account.
 */
export const useAccountCreate = (options: UseMutationOptions<Account, unknown, AccountCreateRequest> = {}) => {
  return useMutation((req: AccountCreateRequest) => AccountsAPI.create(req), options);
};

/**
 * Mutation to update the basic info (name) of an account.
 *
 * NOTE: This will update the current account in the auth state on success.
 */
export const useAccountUpdate = (accountId: number, options: UseMutationOptions<Account, unknown, string> = {}) => {
  const { onSetCurrentAccount } = useAuthState();
  return useMutation((name: string) => AccountsAPI.update(accountId, name), {
    ...options,
    onSuccess: (data, variables, context) => {
      onSetCurrentAccount(data);
      return options.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * Mutation to switch the current account.
 *
 * NOTE: This will update the current account in the auth state.
 */
export const useAccountSwitch = (
  options: UseMutationOptions<Account & { permissions: UserPermissionType[] }, unknown, number> = {},
) => {
  const { onSetCurrentAccount } = useAuthState();
  return useMutation((id: number | string) => ProfileAPI.switchAccount(id), {
    ...options,
    onSuccess: (data, variables, context) => {
      onSetCurrentAccount(data);
      return options.onSuccess?.(data, variables, context);
    },
  });
};

/**
 * Export Users from Account.
 */
export const useAccountExportUsers = (
  account_id: number,
  options: UseMutationOptions<AccountExportResponse, unknown, unknown> = {},
) => {
  return useMutation(() => AccountsAPI.exportUsers(account_id), options);
};

/**
 * Get products for the current account.
 */
export const useProducts = <T = Product>(accountId: number, options: UseQueryOptions<Product[], unknown, T[]> = {}) =>
  useQuery<Product[], unknown, T[]>(
    queryKeys.listProducts(accountId),
    () => AccountsAPI.listProducts(accountId),
    options,
  );
